import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FilesetResolver,
  HandLandmarker,
  type HandLandmarkerResult,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';

type ChakraMode = 'auto' | 'rasengan' | 'chidori';
type DetectedGesture = 'rasengan' | 'chidori' | 'charge';

const HAND_CONNECTIONS: Array<[number, number]> = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [0, 5],
  [5, 6],
  [6, 7],
  [7, 8],
  [5, 9],
  [9, 10],
  [10, 11],
  [11, 12],
  [9, 13],
  [13, 14],
  [14, 15],
  [15, 16],
  [13, 17],
  [17, 18],
  [18, 19],
  [19, 20],
  [0, 17],
];

const LANDMARK_MODEL_URL = '/mediapipe/models/hand_landmarker.task';
const TASKS_VISION_WASM_URL = '/mediapipe/wasm';

interface ScreenPoint {
  x: number;
  y: number;
}

interface HandEffect {
  landmarks: NormalizedLandmark[];
  gesture: DetectedGesture;
}

const getPalmCenter = (landmarks: NormalizedLandmark[], width: number, height: number): ScreenPoint => {
  const palmIndexes = [0, 5, 9, 13, 17];
  const center = palmIndexes.reduce(
    (acc, index) => ({
      x: acc.x + (1 - landmarks[index].x) * width,
      y: acc.y + landmarks[index].y * height,
    }),
    { x: 0, y: 0 },
  );

  return {
    x: center.x / palmIndexes.length,
    y: center.y / palmIndexes.length,
  };
};

const toScreenPoint = (landmark: NormalizedLandmark, width: number, height: number): ScreenPoint => ({
  x: (1 - landmark.x) * width,
  y: landmark.y * height,
});

const countExtendedFingers = (landmarks: NormalizedLandmark[]) => {
  const fingerPairs = [
    [8, 6],
    [12, 10],
    [16, 14],
    [20, 18],
  ];

  const fourFingers = fingerPairs.reduce((count, [tip, pip]) => {
    return count + (landmarks[tip].y < landmarks[pip].y - 0.025 ? 1 : 0);
  }, 0);

  const thumbSpread = Math.abs(landmarks[4].x - landmarks[9].x) > 0.13;
  return fourFingers + (thumbSpread ? 1 : 0);
};

const detectGesture = (mode: ChakraMode, landmarks: NormalizedLandmark[]): DetectedGesture => {
  if (mode === 'rasengan' || mode === 'chidori') return mode;

  const extendedFingers = countExtendedFingers(landmarks);
  const thumbIndexDistance = Math.hypot(landmarks[4].x - landmarks[8].x, landmarks[4].y - landmarks[8].y);

  if (extendedFingers >= 4) return 'chidori';
  if (extendedFingers <= 1 || thumbIndexDistance < 0.055) return 'rasengan';
  return 'charge';
};

const drawHandGuide = (
  ctx: CanvasRenderingContext2D,
  landmarks: NormalizedLandmark[],
  width: number,
  height: number,
) => {
  ctx.save();
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(126, 211, 255, 0.85)';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';

  HAND_CONNECTIONS.forEach(([start, end]) => {
    const from = toScreenPoint(landmarks[start], width, height);
    const to = toScreenPoint(landmarks[end], width, height);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  });

  landmarks.forEach((landmark) => {
    const point = toScreenPoint(landmark, width, height);
    ctx.beginPath();
    ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.restore();
};

const drawRasengan = (
  ctx: CanvasRenderingContext2D,
  center: ScreenPoint,
  tick: number,
  scale: number,
) => {
  const radius = Math.max(34, 58 * scale);
  const glow = ctx.createRadialGradient(center.x, center.y, 4, center.x, center.y, radius * 1.7);
  glow.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
  glow.addColorStop(0.32, 'rgba(80, 180, 255, 0.8)');
  glow.addColorStop(1, 'rgba(18, 78, 255, 0)');

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius * 1.7, 0, Math.PI * 2);
  ctx.fill();

  for (let i = 0; i < 8; i += 1) {
    const rotation = tick * 0.035 + (i * Math.PI) / 4;
    ctx.strokeStyle = i % 2 === 0 ? 'rgba(173, 232, 255, 0.95)' : 'rgba(55, 145, 255, 0.9)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.ellipse(
      center.x,
      center.y,
      radius * (0.95 + Math.sin(tick * 0.03 + i) * 0.12),
      radius * 0.35,
      rotation,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
  }

  ctx.restore();
};

const drawChidori = (
  ctx: CanvasRenderingContext2D,
  landmarks: NormalizedLandmark[],
  width: number,
  height: number,
  tick: number,
) => {
  const palm = getPalmCenter(landmarks, width, height);
  const tips = [4, 8, 12, 16, 20].map((index) => toScreenPoint(landmarks[index], width, height));

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.shadowColor = '#8ee7ff';
  ctx.shadowBlur = 18;

  tips.forEach((tip, tipIndex) => {
    for (let bolt = 0; bolt < 2; bolt += 1) {
      const segments = 5;
      ctx.beginPath();
      ctx.moveTo(palm.x, palm.y);

      for (let i = 1; i <= segments; i += 1) {
        const progress = i / segments;
        const jitter = Math.sin(tick * 0.2 + tipIndex * 3 + bolt * 11 + i) * 20;
        const x = palm.x + (tip.x - palm.x) * progress + jitter * (1 - progress * 0.35);
        const y = palm.y + (tip.y - palm.y) * progress + Math.cos(tick * 0.24 + i * 2) * 14;
        ctx.lineTo(x, y);
      }

      ctx.strokeStyle = bolt === 0 ? 'rgba(255, 255, 255, 0.95)' : 'rgba(87, 214, 255, 0.8)';
      ctx.lineWidth = bolt === 0 ? 2.4 : 5;
      ctx.stroke();
    }
  });

  ctx.fillStyle = 'rgba(130, 232, 255, 0.35)';
  ctx.beginPath();
  ctx.arc(palm.x, palm.y, 42 + Math.sin(tick * 0.14) * 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const drawChargeAura = (
  ctx: CanvasRenderingContext2D,
  center: ScreenPoint,
  tick: number,
) => {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = 'rgba(147, 197, 253, 0.75)';
  ctx.lineWidth = 2;

  for (let i = 0; i < 3; i += 1) {
    const radius = 26 + i * 14 + Math.sin(tick * 0.08 + i) * 4;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
};

const HandChakraStudio: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const landmarkerRef = useRef<HandLandmarker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const modeRef = useRef<ChakraMode>('auto');
  const [status, setStatus] = useState('카메라를 켜면 손동작을 인식해 차크라 효과를 합성합니다.');
  const [mode, setMode] = useState<ChakraMode>('auto');
  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gestureLabel, setGestureLabel] = useState('대기 중');

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const stopCamera = useCallback((nextStatus = '카메라가 꺼졌습니다.') => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    setIsRunning(false);
    setGestureLabel('대기 중');
    setStatus(nextStatus);
  }, []);

  const ensureLandmarker = useCallback(async () => {
    if (landmarkerRef.current) return landmarkerRef.current;

    const vision = await FilesetResolver.forVisionTasks(TASKS_VISION_WASM_URL);

    landmarkerRef.current = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: LANDMARK_MODEL_URL,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numHands: 2,
    });

    return landmarkerRef.current;
  }, []);

  const drawFrame = useCallback((result: HandLandmarkerResult, tick: number) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!video || !canvas || !ctx) return;

    const width = canvas.clientWidth || video.videoWidth;
    const height = canvas.clientHeight || video.videoHeight;
    const pixelRatio = window.devicePixelRatio || 1;

    if (canvas.width !== Math.floor(width * pixelRatio) || canvas.height !== Math.floor(height * pixelRatio)) {
      canvas.width = Math.floor(width * pixelRatio);
      canvas.height = Math.floor(height * pixelRatio);
    }

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const effects: HandEffect[] = result.landmarks.map((landmarks) => ({
      landmarks,
      gesture: detectGesture(modeRef.current, landmarks),
    }));

    effects.forEach(({ landmarks, gesture }) => {
      const palm = getPalmCenter(landmarks, width, height);
      drawHandGuide(ctx, landmarks, width, height);

      if (gesture === 'rasengan') {
        drawRasengan(ctx, palm, tick, 1);
      } else if (gesture === 'chidori') {
        drawChidori(ctx, landmarks, width, height, tick);
      } else {
        drawChargeAura(ctx, palm, tick);
      }
    });

    const primaryGesture = effects[0]?.gesture;
    if (primaryGesture === 'rasengan') setGestureLabel('나선환');
    else if (primaryGesture === 'chidori') setGestureLabel('치도리');
    else if (primaryGesture === 'charge') setGestureLabel('차크라 모으는 중');
    else setGestureLabel('손을 화면에 보여주세요');
  }, []);

  const startDetectionLoop = useCallback((landmarker: HandLandmarker) => {
    const detect = () => {
      const video = videoRef.current;
      if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        const result = landmarker.detectForVideo(video, performance.now());
        drawFrame(result, performance.now());
      }

      rafRef.current = requestAnimationFrame(detect);
    };

    rafRef.current = requestAnimationFrame(detect);
  }, [drawFrame]);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('이 브라우저에서는 카메라 접근을 지원하지 않습니다.');
      return;
    }

    setIsLoading(true);
    setStatus('손 인식 모델과 카메라를 준비하고 있습니다...');

    try {
      const [landmarker, stream] = await Promise.all([
        ensureLandmarker(),
        navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        }),
      ]);

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setIsRunning(true);
      setStatus('손을 펼치면 치도리, 주먹을 쥐거나 손가락을 모으면 나선환이 나옵니다.');
      startDetectionLoop(landmarker);
    } catch (error) {
      console.error('손 인식 도구 실행 중 오류:', error);
      stopCamera('카메라 권한 또는 손 인식 모델 로딩에 실패했습니다. 권한을 확인한 뒤 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  }, [ensureLandmarker, startDetectionLoop, stopCamera]);

  useEffect(() => {
    return () => {
      stopCamera();
      landmarkerRef.current?.close();
      landmarkerRef.current = null;
    };
  }, [stopCamera]);

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 56px)',
        background: 'radial-gradient(circle at top, #172554 0%, #020617 52%, #000 100%)',
        color: 'white',
        padding: '24px',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: '#93c5fd', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>AI 수업 도구</div>
            <h1 style={{ margin: 0, fontSize: 34, lineHeight: '44px', letterSpacing: '-0.02em' }}>
              손동작 차크라 스튜디오
            </h1>
            <p style={{ margin: '10px 0 0', color: 'rgba(255,255,255,0.72)', fontSize: 15 }}>
              웹캠으로 손을 인식해서 나선환과 치도리 효과를 실시간으로 합성합니다.
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'rgba(15, 23, 42, 0.78)',
              border: '1px solid rgba(148, 163, 184, 0.28)',
              borderRadius: 18,
              padding: 12,
              height: 'fit-content',
            }}
          >
            {(['auto', 'rasengan', 'chidori'] as ChakraMode[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setMode(item)}
                style={{
                  border: 'none',
                  borderRadius: 12,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  color: mode === item ? '#020617' : 'white',
                  background: mode === item ? '#93c5fd' : 'rgba(255,255,255,0.08)',
                  fontWeight: 700,
                }}
              >
                {item === 'auto' ? 'AI 자동' : item === 'rasengan' ? '나선환' : '치도리'}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            minHeight: 320,
            overflow: 'hidden',
            borderRadius: 28,
            background: '#020617',
            border: '1px solid rgba(147, 197, 253, 0.35)',
            boxShadow: '0 24px 80px rgba(37, 99, 235, 0.24)',
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
              opacity: isRunning ? 1 : 0.32,
            }}
          />
          <canvas
            ref={canvasRef}
            aria-label="손 인식 차크라 효과 화면"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
          />

          {!isRunning && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 16,
                background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.12), rgba(2, 6, 23, 0.72))',
                textAlign: 'center',
                padding: 24,
              }}
            >
              <div style={{ fontSize: 56, filter: 'drop-shadow(0 0 18px rgba(147, 197, 253, 0.85))' }}>AI</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>카메라를 켜고 손을 보여주세요</div>
              <div style={{ color: 'rgba(255,255,255,0.72)', maxWidth: 520, lineHeight: '24px' }}>
                모든 처리는 브라우저에서 실행됩니다. 수업 중 발표 화면에 띄워두면 학생들이 직접 손동작으로 효과를 만들 수 있습니다.
              </div>
            </div>
          )}

          <div
            style={{
              position: 'absolute',
              left: 18,
              top: 18,
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              zIndex: 2,
            }}
          >
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                background: 'rgba(2, 6, 23, 0.72)',
                border: '1px solid rgba(255,255,255,0.18)',
                fontWeight: 800,
              }}
            >
              인식: {gestureLabel}
            </div>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                background: 'rgba(2, 6, 23, 0.72)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#bfdbfe',
                fontWeight: 700,
              }}
            >
              {mode === 'auto' ? 'AI 자동 모드' : mode === 'rasengan' ? '나선환 고정' : '치도리 고정'}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
          }}
        >
          <div
            style={{
              background: 'rgba(15, 23, 42, 0.78)',
              border: '1px solid rgba(148, 163, 184, 0.26)',
              borderRadius: 20,
              padding: 18,
            }}
          >
            <div style={{ color: '#93c5fd', fontWeight: 800, marginBottom: 8 }}>사용법</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '24px', fontSize: 14 }}>
              손을 활짝 펼치면 치도리, 주먹을 쥐거나 엄지와 검지를 모으면 나선환으로 인식합니다.
            </div>
          </div>

          <div
            style={{
              background: 'rgba(15, 23, 42, 0.78)',
              border: '1px solid rgba(148, 163, 184, 0.26)',
              borderRadius: 20,
              padding: 18,
            }}
          >
            <div style={{ color: '#93c5fd', fontWeight: 800, marginBottom: 8 }}>상태</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '24px', fontSize: 14 }}>{status}</div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
            <button
              type="button"
              onClick={startCamera}
              disabled={isRunning || isLoading}
              style={{
                flex: 1,
                border: 'none',
                borderRadius: 18,
                padding: '16px 18px',
                cursor: isRunning || isLoading ? 'not-allowed' : 'pointer',
                background: isRunning || isLoading ? 'rgba(148, 163, 184, 0.45)' : '#3b82f6',
                color: 'white',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              {isLoading ? '준비 중...' : isRunning ? '실행 중' : '카메라 켜기'}
            </button>
            <button
              type="button"
              onClick={() => stopCamera()}
              disabled={!isRunning && !isLoading}
              style={{
                flex: 1,
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 18,
                padding: '16px 18px',
                cursor: !isRunning && !isLoading ? 'not-allowed' : 'pointer',
                background: 'rgba(255,255,255,0.08)',
                color: 'white',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              카메라 끄기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandChakraStudio;
